// ═══════════════════════════════════════════════════════════════
// PIPELINE DRAWER — Slide-out member detail panel
// Path: src/components/cultivation/PipelineDrawer.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { FONT_FAMILY } from "../../constants/appConfig";
import {
  PROSPECT_STAGE,
  SCORING_STAGES,
  ALL_PIPELINE_STAGES,
  getDaysColor,
  getPipelineStageColors,
  getPipelineAction,
  PIPELINE_URGENCY,
  CONTACT_TYPE_ICONS,
  getStageForScore,
} from "../../utils/cultivationUtils";

export const PipelineDrawer = ({
  member,
  palette,
  onClose,
  onOpenProfile,
  onUpdateMember,
  onAddContactLog,
  onShowMessage,
}) => {
  const engagement = member.engagement;
  const isProspect = member.pathway === "prospect";
  const stage = isProspect
    ? PROSPECT_STAGE
    : getStageForScore(engagement.score);
  const stageColors = getPipelineStageColors(palette)[stage.key];
  const pipelineAction = getPipelineAction(member, engagement);
  const urgStyle = PIPELINE_URGENCY[pipelineAction.urgency];
  const logs = (member.contactLog || [])
    .map((l) => ({ ...l, _date: new Date(l.date || l.createdAt) }))
    .sort((a, b) => b._date - a._date);
  const plannedStep = member.custom_fields?.cultivationNextStep;
  const targetDate = member.custom_fields?.cultivationTargetDate;

  const giftLogs = logs.filter(
    (l) => l.type === "gift" || l.type === "donation"
  );
  const lastGift = giftLogs.length > 0 ? giftLogs[0] : null;

  const [showQuickLog, setShowQuickLog] = useState(false);
  const [quickLogType, setQuickLogType] = useState("phone");
  const [quickLogNote, setQuickLogNote] = useState("");
  const [quickLogSaving, setQuickLogSaving] = useState(false);
  const [showStageMove, setShowStageMove] = useState(false);

  const QUICK_LOG_TYPES = [
    { id: "phone", label: "📞 Phone" },
    { id: "email", label: "📧 Email" },
    { id: "meeting", label: "🤝 Meeting" },
    { id: "coffee", label: "☕ Coffee" },
    { id: "event", label: "🎪 Event" },
    { id: "note", label: "📝 Note" },
  ];

  const handleQuickLog = async () => {
    if (!quickLogNote.trim()) {
      onShowMessage?.("Add a note about the contact", "error");
      return;
    }
    setQuickLogSaving(true);
    try {
      await onAddContactLog?.(member.id, {
        type: quickLogType,
        notes: quickLogNote.trim(),
        date: new Date().toISOString().slice(0, 10),
      });
      setQuickLogNote("");
      setShowQuickLog(false);
      onShowMessage?.("Contact logged");
    } catch (err) {
      onShowMessage?.("Error logging contact", "error");
    }
    setQuickLogSaving(false);
  };

  const handleStageMove = async (targetStageKey) => {
    if (targetStageKey === "prospect") {
      try {
        await onUpdateMember?.(member.id, { pathway: "prospect" });
        onShowMessage?.("Moved to Prospects");
        setShowStageMove(false);
      } catch (err) {
        onShowMessage?.("Error moving stage", "error");
      }
      return;
    }
    const targetStage = SCORING_STAGES.find((s) => s.key === targetStageKey);
    if (!targetStage) return;
    const pathwayId = targetStageKey === "cold" ? "unengaged" : targetStageKey;
    try {
      await onUpdateMember?.(member.id, { pathway: pathwayId });
      onShowMessage?.(`Moved to ${targetStage.label}`);
      setShowStageMove(false);
    } catch (err) {
      onShowMessage?.("Error moving stage", "error");
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.25)",
          zIndex: 999,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: Math.min(
            400,
            typeof window !== "undefined" ? window.innerWidth - 20 : 380
          ),
          background: palette.cardBg,
          boxShadow: "-2px 0 20px rgba(0,0,0,0.12)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          animation: "pipelineDrawerSlide 0.2s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 16px",
            background: stageColors.headerBg,
            color: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 17,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {member.firstName} {member.lastName}
              </div>
              {member.organization && (
                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 1 }}>
                  {member.organization}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                color: "#fff",
                fontSize: 16,
                cursor: "pointer",
                padding: "2px 8px",
                borderRadius: 4,
                fontWeight: 700,
              }}
            >
              ✕
            </button>
          </div>
          <div
            style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 4,
                background: "rgba(255,255,255,0.2)",
              }}
            >
              Stage: {stage.label}
            </span>
            {isProspect && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                Prospect
              </span>
            )}
            {member.isBoardMember && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                Board Member
              </span>
            )}
            {member.isDonor && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                Donor
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {/* Key Numbers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                background: "#f9fafb",
                padding: 10,
                borderRadius: 6,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: getDaysColor(engagement.daysSinceContact),
                }}
              >
                {engagement.daysSinceContact === null
                  ? "—"
                  : engagement.daysSinceContact}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                Days Since
                <br />
                Contact
              </div>
            </div>
            <div
              style={{
                background: "#f9fafb",
                padding: 10,
                borderRadius: 6,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: "#374151" }}>
                {engagement.totalContacts}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                Total
                <br />
                Contacts
              </div>
            </div>
            <div
              style={{
                background: "#f9fafb",
                padding: 10,
                borderRadius: 6,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: stageColors.color,
                }}
              >
                {engagement.score}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#6b7280",
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                Engagement
                <br />
                Score
              </div>
            </div>
          </div>

          {/* Relationship status */}
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "8px 10px",
              borderRadius: 6,
              marginBottom: 16,
              textAlign: "center",
              lineHeight: 1.4,
              background:
                engagement.daysSinceContact === null
                  ? "#fef2f2"
                  : engagement.daysSinceContact > 30
                  ? "#fef2f2"
                  : engagement.daysSinceContact > 14
                  ? "#fffbeb"
                  : "#f0fdf4",
              color:
                engagement.daysSinceContact === null
                  ? "#991b1b"
                  : engagement.daysSinceContact > 30
                  ? "#991b1b"
                  : engagement.daysSinceContact > 14
                  ? "#92400e"
                  : "#166534",
            }}
          >
            {engagement.daysSinceContact === null
              ? "⚠️ Never contacted — make the first move today"
              : engagement.daysSinceContact > 30
              ? "⚠️ This relationship is cooling — reconnect this week"
              : engagement.daysSinceContact > 14
              ? "Getting stale — schedule a check-in"
              : "✓ On track — maintain momentum"}
          </div>

          {/* Last Gift */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              Last Gift
            </div>
            {lastGift ? (
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  padding: "10px 12px",
                  fontSize: 13,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#166534" }}>
                    {lastGift.subject || lastGift.notes || "Gift recorded"}
                  </span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>
                    {lastGift.date ||
                      new Date(lastGift.createdAt).toISOString().slice(0, 10)}
                  </span>
                </div>
              </div>
            ) : (
              <div
                style={{
                  background: "#fef9f0",
                  border: "1px solid #fed7aa",
                  borderRadius: 6,
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "#92400e",
                }}
              >
                {isProspect
                  ? "No gifts yet — focus on building the relationship first"
                  : "No gifts yet — is this person ready for an ask?"}
              </div>
            )}
          </div>

          {/* What to do next */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              What to do next
            </div>
            <div
              style={{
                background: urgStyle.bg,
                border: `1px solid ${urgStyle.border}`,
                borderRadius: 6,
                padding: "10px 12px",
                fontSize: 13,
                fontWeight: 600,
                color: urgStyle.text,
                lineHeight: 1.4,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: urgStyle.dot,
                  marginRight: 8,
                  verticalAlign: "middle",
                }}
              />
              {pipelineAction.text}
            </div>
          </div>

          {/* Planned step */}
          {plannedStep && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  marginBottom: 6,
                }}
              >
                My planned step
              </div>
              <div
                style={{
                  background: "#eef2ff",
                  border: "1px solid #c7d2fe",
                  borderRadius: 6,
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "#4338ca",
                  lineHeight: 1.3,
                }}
              >
                {plannedStep}
                {targetDate && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6366f1",
                      marginTop: 4,
                      fontWeight: 600,
                    }}
                  >
                    Target: {targetDate}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Contact Log */}
          <div style={{ marginBottom: 16 }}>
            {!showQuickLog ? (
              <button
                onClick={() => setShowQuickLog(true)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  background: `${stageColors.color}10`,
                  border: `1px dashed ${stageColors.color}40`,
                  borderRadius: 6,
                  color: stageColors.color,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: FONT_FAMILY,
                  transition: "all 0.15s",
                }}
              >
                ✍️ I made contact — log it
              </button>
            ) : (
              <div
                style={{
                  background: "#f9fafb",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#374151",
                    marginBottom: 8,
                  }}
                >
                  Quick Contact Log
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginBottom: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {QUICK_LOG_TYPES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setQuickLogType(t.id)}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        border:
                          quickLogType === t.id
                            ? `2px solid ${stageColors.color}`
                            : `1px solid ${palette.cardBorder}`,
                        background:
                          quickLogType === t.id
                            ? `${stageColors.color}15`
                            : palette.cardBg,
                        color:
                          quickLogType === t.id ? stageColors.color : "#6b7280",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={quickLogNote}
                  onChange={(e) => setQuickLogNote(e.target.value)}
                  placeholder="What happened? (e.g. Discussed annual fund, very interested)"
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    fontSize: 13,
                    border: `1px solid ${palette.cardBorder}`,
                    fontFamily: FONT_FAMILY,
                    resize: "vertical",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    onClick={handleQuickLog}
                    disabled={quickLogSaving}
                    style={{
                      flex: 1,
                      padding: "8px 14px",
                      borderRadius: 6,
                      border: "none",
                      background: stageColors.headerBg,
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: quickLogSaving ? "wait" : "pointer",
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {quickLogSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => {
                      setShowQuickLog(false);
                      setQuickLogNote("");
                    }}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 6,
                      border: `1px solid ${palette.cardBorder}`,
                      background: palette.cardBg,
                      color: "#6b7280",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Move Stage */}
          <div style={{ marginBottom: 16 }}>
            {!showStageMove ? (
              <button
                onClick={() => setShowStageMove(true)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  background: "#f3f4f6",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 6,
                  color: "#374151",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: FONT_FAMILY,
                  transition: "all 0.15s",
                }}
              >
                {isProspect
                  ? "🎉 Convert to member or move stage"
                  : "📈 Advance this relationship"}
              </button>
            ) : (
              <div
                style={{
                  background: "#f9fafb",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#374151",
                    marginBottom: 8,
                  }}
                >
                  Move to stage:
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {ALL_PIPELINE_STAGES.map((s) => {
                    const sc = getPipelineStageColors(palette)[s.key];
                    const isCurrent =
                      (s.key === "prospect" && isProspect) ||
                      (!isProspect && s.key === stage.key);
                    return (
                      <button
                        key={s.key}
                        onClick={() => !isCurrent && handleStageMove(s.key)}
                        disabled={isCurrent}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 12px",
                          borderRadius: 6,
                          border: isCurrent
                            ? `2px solid ${sc.color}`
                            : `1px solid ${palette.cardBorder}`,
                          background: isCurrent ? `${sc.color}10` : palette.cardBg,
                          cursor: isCurrent ? "default" : "pointer",
                          transition: "all 0.15s",
                          opacity: isCurrent ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: sc.headerBg,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: isCurrent ? sc.color : "#374151",
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          {s.label}
                        </span>
                        {isCurrent && (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#9ca3af",
                              marginLeft: "auto",
                            }}
                          >
                            current
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setShowStageMove(false)}
                  style={{
                    width: "100%",
                    marginTop: 8,
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: `1px solid ${palette.cardBorder}`,
                    background: palette.cardBg,
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Conversation Trail */}
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              Conversation trail
            </div>
            {logs.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  background: "#fef2f2",
                  borderRadius: 6,
                  fontSize: 13,
                  color: "#991b1b",
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                No contacts recorded — reach out today
              </div>
            ) : (
              logs.slice(0, 5).map((log, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 0",
                    borderBottom:
                      i < Math.min(logs.length, 5) - 1
                        ? "1px solid #f3f4f6"
                        : "none",
                  }}
                >
                  <span style={{ fontSize: 16 }}>
                    {CONTACT_TYPE_ICONS[log.type] || "📝"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                        textTransform: "capitalize",
                      }}
                    >
                      {log.type}
                    </span>
                    {log.notes && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#6b7280",
                          marginTop: 1,
                          lineHeight: 1.3,
                          maxHeight: 32,
                          overflow: "hidden",
                        }}
                      >
                        {log.notes}
                      </div>
                    )}
                  </div>
                  <span
                    style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}
                  >
                    {log.date ||
                      new Date(log.createdAt).toISOString().slice(0, 10)}
                  </span>
                </div>
              ))
            )}
            {logs.length > 5 && (
              <div
                style={{
                  fontSize: 11,
                  color: "#9ca3af",
                  textAlign: "center",
                  marginTop: 6,
                }}
              >
                + {logs.length - 5} more — open full profile to see all
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${palette.cardBorder}` }}>
          <button
            onClick={() => onOpenProfile?.(member)}
            style={{
              width: "100%",
              padding: "10px 16px",
              background: stageColors.headerBg,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              transition: "opacity 0.15s",
              fontFamily: FONT_FAMILY,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Open Full Profile →
          </button>
        </div>
      </div>
    </>
  );
};
